export const SUPPORT_CATEGORIES = ["order", "parcel", "payment", "after_sales", "other"] as const;
export const SUPPORT_STATUSES = ["open", "waiting_customer", "resolved"] as const;

export type SupportCategory = (typeof SUPPORT_CATEGORIES)[number];
export type SupportStatus = (typeof SUPPORT_STATUSES)[number];

export type SupportMessage = {
  id: string;
  senderType: "customer" | "staff" | "system";
  body: string;
  attachments?: Array<{
    id: string;
    fileName: string;
    fileUrl: string;
    fileMime: string;
    fileSize: number;
    createdAt: string;
  }>;
  createdAt: string;
};

export type SupportTicket = {
  id: string;
  subject: string;
  category: SupportCategory;
  status: SupportStatus;
  priority: "normal" | "urgent";
  relatedOrderId?: string;
  relatedProductId?: string;
  relatedWarehouseItemId?: string;
  relatedParcelId?: string;
  createdAt: string;
  updatedAt: string;
  messages: SupportMessage[];
};

export type SupportContextKey =
  | "orderId"
  | "productId"
  | "warehouseItemId"
  | "parcelId"
  | "refundId"
  | "rehearsalId"
  | "transactionId";

export type SupportContextItem = {
  key: SupportContextKey;
  label: string;
  value: string;
  persistence: "linked" | "message";
};

export type SupportContext = {
  category: SupportCategory;
  topic: string;
  items: SupportContextItem[];
  subject: string;
};

export type SupportFilters = {
  query: string;
  category: "all" | SupportCategory;
  status: "all" | SupportStatus;
  startDate: string;
  endDate: string;
};

const contextDefinitions: Array<{
  key: SupportContextKey;
  label: string;
  persistence: SupportContextItem["persistence"];
}> = [
  { key: "orderId", label: "Order", persistence: "linked" },
  { key: "productId", label: "Product", persistence: "linked" },
  { key: "warehouseItemId", label: "Warehouse item", persistence: "linked" },
  { key: "parcelId", label: "Parcel", persistence: "linked" },
  { key: "refundId", label: "After-sales case", persistence: "message" },
  { key: "rehearsalId", label: "Rehearsal request", persistence: "message" },
  { key: "transactionId", label: "Wallet transaction", persistence: "message" },
];

export const SUPPORT_CATEGORY_LABELS: Record<SupportCategory, string> = {
  order: "Order",
  parcel: "Parcel",
  payment: "Payment",
  after_sales: "After-sales",
  other: "Other",
};

export const SUPPORT_STATUS_LABELS: Record<SupportStatus, string> = {
  open: "Open",
  waiting_customer: "Reply needed",
  resolved: "Resolved",
};

export function parseSupportContext(search: string): SupportContext | null {
  const query = new URLSearchParams(search);
  const topic = query.get("topic")?.trim() ?? "";
  const items = contextDefinitions.flatMap((definition) => {
    const value = query.get(definition.key)?.trim();
    return value ? [{ ...definition, value }] : [];
  });
  if (!items.length) return null;

  const category: SupportCategory =
    topic === "parcel" ? "parcel"
      : topic === "payment" || topic === "wallet" || items.some((item) => item.key === "transactionId") ? "payment"
        : topic === "after_sales" || items.some((item) => item.key === "refundId") ? "after_sales"
          : topic === "qc" || topic === "warehouse" || topic === "rehearsal" || items.some((item) => item.key === "orderId") ? "order"
            : "other";
  const subjectPrefix =
    topic === "wallet" ? "Wallet transaction help"
      : topic === "qc" ? "QC help"
      : topic === "warehouse" ? "Warehouse help"
        : topic === "rehearsal" ? "Rehearsal help"
          : category === "parcel" ? "Parcel help"
            : category === "after_sales" ? "After-sales help"
              : category === "payment" ? "Payment help"
                : category === "order" ? "Order help"
                  : "Support request";
  const first = items[0];
  return { category, topic, items, subject: `${subjectPrefix} · ${first.label} ${first.value}` };
}

export function buildTicketRequest(input: {
  subject: string;
  category: SupportCategory;
  message: string;
  context: SupportContext | null;
  includedContextLabel?: string;
}) {
  const messageItems = input.context?.items.filter((item) => item.persistence === "message") ?? [];
  const linkedItems = input.context?.items.filter((item) => item.persistence === "linked") ?? [];
  const includedContext = messageItems.length
    ? `\n\n${input.includedContextLabel ?? "Context included in your message"}: ${messageItems.map((item) => `${item.label}: ${item.value}`).join(" · ")}`
    : "";
  const valueFor = (key: SupportContextKey) => linkedItems.find((item) => item.key === key)?.value;

  return {
    subject: input.subject.trim(),
    category: input.category,
    message: `${input.message.trim()}${includedContext}`,
    relatedOrderId: valueFor("orderId"),
    relatedProductId: valueFor("productId"),
    relatedWarehouseItemId: valueFor("warehouseItemId"),
    relatedParcelId: valueFor("parcelId"),
  };
}

export function ticketMatchesContext(ticket: SupportTicket, context: SupportContext | null) {
  if (!context) return false;
  const linked = context.items.filter((item) => item.persistence === "linked");
  if (!linked.length) return false;
  const ticketValues: Partial<Record<SupportContextKey, string | undefined>> = {
    orderId: ticket.relatedOrderId,
    productId: ticket.relatedProductId,
    warehouseItemId: ticket.relatedWarehouseItemId,
    parcelId: ticket.relatedParcelId,
  };
  return linked.every((item) => ticketValues[item.key] === item.value);
}

export function filterSupportTickets(tickets: SupportTicket[], filters: SupportFilters) {
  const query = filters.query.trim().toLocaleLowerCase();
  const start = filters.startDate ? new Date(`${filters.startDate}T00:00:00`).getTime() : Number.NEGATIVE_INFINITY;
  const end = filters.endDate ? new Date(`${filters.endDate}T23:59:59.999`).getTime() : Number.POSITIVE_INFINITY;

  return tickets.filter((ticket) => {
    const updated = new Date(ticket.updatedAt).getTime();
    const haystack = [
      ticket.id,
      ticket.subject,
      ticket.category,
      ticket.relatedOrderId,
      ticket.relatedProductId,
      ticket.relatedWarehouseItemId,
      ticket.relatedParcelId,
    ].filter(Boolean).join(" ").toLocaleLowerCase();
    return (!query || haystack.includes(query))
      && (filters.category === "all" || ticket.category === filters.category)
      && (filters.status === "all" || ticket.status === filters.status)
      && Number.isFinite(updated) && updated >= start && updated <= end;
  });
}

export function supportErrorMessage(error: unknown, fallback = "Support is temporarily unavailable.") {
  const code = error instanceof Error ? error.message : typeof error === "string" ? error : "";
  if (code === "USER_REQUIRED" || code === "USER_NOT_FOUND") return "Your session has expired. Sign in again to continue.";
  if (code === "CUSTOMER_INACTIVE") return "This account is inactive and cannot create new Support requests.";
  if (code === "IDEMPOTENCY_KEY_REQUIRED" || code === "IDEMPOTENCY_KEY_INVALID") return "Support could not start a safe request attempt. Refresh and try again.";
  if (code === "IDEMPOTENCY_KEY_PAYLOAD_MISMATCH") return "The request changed during retry. Review it and submit again.";
  if (code === "IDEMPOTENCY_REQUEST_IN_PROGRESS") return "This request is still being confirmed. Wait a moment, then retry with the same details.";
  if (code === "SUPPORT_RELATED_ENTITY_NOT_FOUND") return "This context is unavailable for your account. Open the related item and try again.";
  if (code === "SUPPORT_TICKET_NOT_FOUND" || code === "SUPPORT_TICKET_FORBIDDEN") return "This ticket is unavailable for your account.";
  if (code === "SUPPORT_TICKET_REQUIRED") return "Add a subject and message before sending.";
  if (code === "SUPPORT_MESSAGE_REQUIRED") return "Write a message before sending.";
  if (code === "SUPPORT_ACTION_INVALID" || code === "SUPPORT_STATUS_TRANSITION_INVALID") return "This ticket can no longer make that status change. Refresh it and try again.";
  if (code === "SUPPORT_READBACK_PENDING") return "The update was accepted, but its latest status could not be confirmed. Refresh the ticket before trying again.";
  if (code === "Failed to fetch") return "Support could not be reached. Check your connection and retry.";
  return fallback;
}

export function formatSupportDate(value: string, includeTime = false, locale: "en" | "zh" = "en") {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return locale === "zh" ? "日期不可用" : "Date unavailable";
  return new Intl.DateTimeFormat(locale === "zh" ? "zh-CN" : "en-US", includeTime
    ? { year: "numeric", month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit" }
    : { year: "numeric", month: "short", day: "2-digit" }).format(date);
}
